import { all, put, takeLatest, call, select } from 'redux-saga/effects';
import {
  AreaOfInterestActionTypes,
  SystemUploadRequestPlayload,
  AoiClippedObject,
  AoiListRequestParams,
  AREA_OF_INTEREST
} from 'types';
import {
  uploadShapeFileSuccess,
  uploadShapeFileFailure,
  putNewAOISuccess,
  putNewAOIFailure,
  getAOIListSuccess,
  getAOIListFailure,
  getCurrentAOIInfoSuccess,
  getCurrentAOIInfoFailure,
  getPreviewAOIInfoSuccess,
  getPreviewAOIInfoFailure,
  setClippedObject,
  setPreviewId,
  initialAOIPageFailure,
  initialAOIPageSuccess,
  getLGABoundariesOptionSuccess,
  getLGABoundariesOptionFailure,
  getLGABoundarySuccess,
  getLGABoundaryFailure
} from './actions';
import {
  getNewUploadLocation,
  getUploadAreaOfInterestJob,
  putNewAreaOfInterestUpload,
  putNewAreaOfInterest,
  getAOI,
  getAOIList,
  getLGABoundariesList,
  getLGABoundary
} from 'services/areaOfInterest.service';
import { fetchJobDetailsById } from 'services/enquiry.service';
import { sortBy } from 'lodash';

const sleep = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

function* handleUploadNewAOI(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const uploadPayload: SystemUploadRequestPlayload = {
        name: request.payload.shapeFile.name,
        mimeType: request.payload.mimeType
      };

      const uploadLocation = yield getNewUploadLocation(uploadPayload);
      if (!uploadLocation.url || !uploadLocation.id) {
        return yield put(
          uploadShapeFileFailure('Unable to get upload location.')
        );
      }

      const uploaded = yield putNewAreaOfInterestUpload(
        uploadLocation.url,
        request.payload.shapeFile
      );
      if (!uploaded.ok) {
        return yield put(uploadShapeFileFailure('Unable to upload file.'));
      }

      const JobResponse = yield getUploadAreaOfInterestJob(uploadLocation.id, {
        transform: request.payload.fileType,
        geometryType: request.payload.geometryType,
        bufferDistance: request.payload.bufferDistance
      });

      if (JobResponse?.error?.message) {
        return yield put(uploadShapeFileFailure(JobResponse.error.message));
      }

      const jobId = JobResponse?.Job?.id;

      if (!jobId) {
        return yield put(uploadShapeFileFailure('Unable to upload file.'));
      }

      let done = false;
      let count = 0;
      const max = 450;
      let result;
      let error;
      do {
        const job = yield fetchJobDetailsById(jobId);
        if (job?.status === 'COMPLETED') {
          done = true;
          result = job?.response.groupId;
        } else if (job?.status === 'FAILED') {
          done = true;
          if (job?.response?.reason) {
            if (job?.response?.reason?.details) {
              error = {
                msg: job?.response?.reason?.message,
                details: job?.response?.reason?.details
              };
            } else {
              error = job?.response?.reason?.message;
            }
          } else if (job?.response?.error) {
            const errorIndex = job.response.error.indexOf('Error: ') + 7;
            error = job.response.error.substr(errorIndex);
          }
        } else {
          count++;
          if (count >= max) {
            done = true;
          } else {
            yield sleep(2000);
          }
        }
      } while (!done);

      if (result) {
        yield put(uploadShapeFileSuccess(result));
      } else {
        yield put(uploadShapeFileFailure(error || 'Unable to upload file.'));
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(uploadShapeFileFailure(errMsg));
    }
  }
}

function* handlePutNewAOI(request: any) {
  if (request?.payload?.data) {
    try {
      const { data, organisationId } = request?.payload;
      const response = yield putNewAreaOfInterest(data, organisationId);
      if (response.error) {
        return yield put(putNewAOIFailure(response.error.message));
      }
      yield put(putNewAOISuccess(response));
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(putNewAOIFailure(errMsg));
    }
  }
}

function* handleGetAOIList(request: any) {
  if (request?.payload?.params) {
    try {
      const { params, organisationId } = request?.payload;
      const response = yield getAOIList(params, organisationId);
      if (response.error) {
        return yield put(getAOIListFailure(response.error.message));
      }
      yield put(getAOIListSuccess(response));
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(putNewAOIFailure(errMsg));
    }
  }
}

function* handleGetCurrentAOIInfo(request: any) {
  if (request?.payload?.organisationId) {
    try {
      const { aoiId, organisationId, returnEnquiryVolume } = request?.payload;
      const response = yield getAOI(organisationId, aoiId, returnEnquiryVolume);
      if (response.error) {
        return yield put(getCurrentAOIInfoFailure(response.error.message));
      }

      if (response.AreaOfInterest) {
        const payload = {
          area: response.AreaOfInterest.area
            ? (response.AreaOfInterest.area / 1e6).toFixed(2)
            : undefined,
          enquiryVolume: response.AreaOfInterest.enquiryVolume?.toString(),
          type: response.AreaOfInterest.type
        };
        yield put(getCurrentAOIInfoSuccess(payload));
      } else {
        yield put(getCurrentAOIInfoFailure('Unable to get current AOI Info'));
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(getCurrentAOIInfoFailure(errMsg));
    }
  }
}

function* handleGetPreviewAOIInfo(request: any) {
  if (request?.payload?.data) {
    try {
      let response;
      const { data, organisationId } = request?.payload;
      response = yield putNewAreaOfInterest(data, organisationId, true, true);
      if (
        response?.message === 'Endpoint request timed out' ||
        (response instanceof Error &&
          response.message.search('Network Error') !== -1)
      ) {
        //call another time with returnEnquiryVolume to false when response is empty
        response = yield putNewAreaOfInterest(
          data,
          organisationId,
          true,
          false
        );
      }
      if (response.error) {
        return yield put(getPreviewAOIInfoFailure(response.error.message));
      }
      if (response.warning) {
        const clippedObject: AoiClippedObject = {
          warning: response.warning
        };
        yield put(setClippedObject(clippedObject));
      } else {
        yield put(
          setClippedObject({
            warning: ''
          })
        );
      }
      if (response.previewId) {
        yield put(setPreviewId({ previewId: response.previewId }));
      }
      if (response.Info) {
        const newArea = response.Info.clippedArea || response.Info.newArea;
        const info = {
          area: (newArea / 1e6).toFixed(2),
          enquiryVolume: response.Info.newEnquiryVolume?.toString()
        };
        yield put(getPreviewAOIInfoSuccess(info));
      } else {
        yield put(getPreviewAOIInfoFailure('Unable to get preview AOI Info'));
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(getPreviewAOIInfoFailure(errMsg));
    }
  }
}

function* handleGetLGABoundariesOption(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const promises = request.payload.map((state: string) => {
        return getLGABoundariesList(state);
      });

      const responses = yield Promise.all(promises);
      let allOptions: any[] = [];
      responses.forEach((response: any) => {
        const options = Array.isArray(response.LocalGovernmentArea)
          ? response.LocalGovernmentArea.map((lga: any) => {
              return { label: lga.name, value: String(lga.id) };
            })
          : [];
        allOptions = [...allOptions, ...options];
      });
      allOptions = sortBy(allOptions, ['label']);
      yield put(getLGABoundariesOptionSuccess(allOptions));
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(getLGABoundariesOptionFailure(errMsg));
    }
  }
}

function* handleGetLGABoundary(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response = yield getLGABoundary(request.payload);
      if (response.error) {
        return yield put(getLGABoundaryFailure(response.error.message));
      }
      if (response.shape) {
        yield put(getLGABoundarySuccess(response.shape));
      } else {
        yield put(getLGABoundaryFailure('Unable to get LGA Boundary'));
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(getLGABoundaryFailure(errMsg));
    }
  }
}

function* handleInitialAOIPage(request: any) {
  if (request?.payload) {
    try {
      const params: AoiListRequestParams = {
        offset: 0,
        limit: AREA_OF_INTEREST.LIST_PAGE_SIZE
      };
      const response = yield getAOIList(params, request.payload);
      if (response?.error) {
        return yield put(initialAOIPageFailure(response.error.message));
      }

      if (Array.isArray(response?.AreasOfInterest)) {
        const { AreasOfInterest } = response;
        const currentActiveAoi =
          AreasOfInterest.length > 0 ? AreasOfInterest[0] : 'empty';
        return yield put(
          initialAOIPageSuccess({
            aoiList: response,
            currentActiveAoi
          })
        );
      }

      yield put(initialAOIPageFailure('Unable to get Area Of Interest data'));
    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.toString();
      yield put(initialAOIPageFailure(errMsg));
    }
  }
}

function* watchUploadNewAOIRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.UPLOAD_SHAPE_FILE_START,
    handleUploadNewAOI
  );
}

function* watchPutNewAOIRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.PUT_NEW_AOI_START,
    handlePutNewAOI
  );
}

function* watchGetAOIListRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.GET_AOI_LIST_START,
    handleGetAOIList
  );
}

function* watchGetCurrentAOIInfoRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.GET_CURRENT_AOI_INFO_START,
    handleGetCurrentAOIInfo
  );
}

function* watchGetPreviewAOIInfoRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.GET_PREVIEW_AOI_INFO_START,
    handleGetPreviewAOIInfo
  );
}

function* watchGetLGABoundariesOptionRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.GET_LGA_BOUNDARIES_OPTION_START,
    handleGetLGABoundariesOption
  );
}

function* watchGetLGABoundaryRequest() {
  yield takeLatest(
    AreaOfInterestActionTypes.GET_LGA_BOUNDARY_START,
    handleGetLGABoundary
  );
}

function* watchInitialAOIPage() {
  yield takeLatest(
    AreaOfInterestActionTypes.INTIAL_AOI_PAGE_START,
    handleInitialAOIPage
  );
}

function* areaOfInterestSagas() {
  yield all([
    call(watchUploadNewAOIRequest),
    call(watchPutNewAOIRequest),
    call(watchGetAOIListRequest),
    call(watchGetCurrentAOIInfoRequest),
    call(watchGetPreviewAOIInfoRequest),
    call(watchGetLGABoundariesOptionRequest),
    call(watchGetLGABoundaryRequest),
    call(watchInitialAOIPage)
  ]);
}

export default areaOfInterestSagas;
